import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ButtonPrimaryMedium from '../../../components/common/ButtonPrimary/ButtonPrimaryMedium';
import TabComponent from '../../../components/common/TabComponent/TabComponent';
import { Icon } from '../../../components/icon/icon';
import SearchComponent from '../../../components/common/SearchComponent/SearchComponent';
import DropdownComponent from '../../../components/common/DropdownComponent/DropdownComponent';
import { Pagination, Table, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import ButtonIconOutlineMedium from '../../../components/common/ButtonIconOutline/ButtonIconOutlineMedium';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ROUTER from '../../../routers/Routers';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../../../store/actions/product.action';
import { getValueFromString } from '../../../utils/data/getValue.utils';
import {
  deleteSalteProduct,
  fetchSaleProducts,
} from '../../../store/actions/sale-product.action';
import Tag from '../../../components/common/Tag/Tag';
import { openMessageSuccess } from '../../../components/common/Message/openMessageSuccess';
import { openMessageError } from '../../../components/common/Message/openMessageError';

const ProductInventoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const products = useSelector(state => state.product.products);

  const [searchParams, setSearchParams] = useSearchParams();
  const productId = searchParams.get('id');

  const inventoryProducts = useMemo(() => {
    return products.data?.filter(item => item.type_design === 'normal') ?? [];
  }, [products.data]);

  const updateSearchParams = useCallback(
    (key, value) => {
      const params = Object.fromEntries([...searchParams]);

      if (value !== null && value !== undefined && value !== '') {
        params[key] = value;
        if (key !== 'offset') {
          params['offset'] = 0;
        }
        setSearchParams({ ...params });
      } else {
        delete params[key];
        setSearchParams(params);
      }
    },
    [searchParams]
  );

  useEffect(() => {
    if (products.isNeedReloadData) dispatch(fetchProducts());
  }, [products.isNeedReloadData, dispatch]);

  useEffect(() => {
    if (inventoryProducts[0]?.id && !productId) {
      updateSearchParams('id', inventoryProducts[0]?.id);
    }
  }, [inventoryProducts]);

  return (
    <div className="flex p-16 flex-col items-start gap-16 self-stretch h-full">
      <div className="flex justify-between items-center self-stretch">
        <h1 className="text-title-medium-emphasized text-black-medium">
          {t('productInventoryRetail')}
        </h1>
        <ButtonPrimaryMedium
          title={t('createProduct')}
          posIcon={<Icon.ICAddCircle />}
          onClick={() => {
            navigate(ROUTER.CMS + ROUTER.CREATE_PRODUCT_INVENTORY);
          }}
        />
      </div>
      <TabComponent
        items={[
          ...inventoryProducts.map(item => ({
            key: item.id,
            label: getValueFromString(item.name),
          })),
        ]}
        activeKey={productId}
        onChange={id => updateSearchParams('id', id)}
        className={'!gap-0 [&_.ant-tabs-content]:h-0 '}
      />
      <RenderListProduct productId={productId} />
    </div>
  );
};

export default ProductInventoryPage;

const RenderListProduct = ({ productId }) => {
  const country_code = process.env.REACT_APP_COUNTRY_CODE ?? 'vn';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const saleProducts = useSelector(state => state.saleProduct.saleProducts);

  const { total } = useMemo(() => {
    return { total: saleProducts.data?.[productId]?.total };
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const offsetParam = searchParams.get('offset') ?? 0;
  const limitParam = searchParams.get('limit') ?? 10;
  const kwParam = searchParams.get('kw');

  const updateSearchParams = useCallback(
    (key, value) => {
      const params = Object.fromEntries([...searchParams]);

      if (value !== null && value !== undefined && value !== '') {
        params[key] = value;
        if (key !== 'offset') {
          params['offset'] = 0;
        }
        setSearchParams({ ...params });
      } else {
        delete params[key];
        setSearchParams(params);
      }
    },
    [searchParams]
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('#'),
        dataIndex: '',
        key: '',
        width: 64,
        render: (_1, _2, index) => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              {index + 1 + Number(offsetParam)}
            </div>
          );
        },
      },
      {
        title: t('Product name'),
        dataIndex: 'name',
        key: 'name',
        width: 180,
        render: value => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <span className="text-body-medium">
                {getValueFromString(value)}
              </span>
            </div>
          );
        },
      },
      {
        title: t('Image'),
        dataIndex: 'thumbnail_url',
        key: 'thumbnail_url',
        width: 104,
        render: value => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <span className="w-64 h-64 flex rounded-[4px] overflow-hidden">
                <img src={value} alt="" className="object-contain" />
              </span>
            </div>
          );
        },
      },
      {
        title: t('Status'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 180,
        render: value => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              {value ? (
                <Tag
                  className="w-fit bg-fills-white border-[1px] border-solid border-outline-default-primary rounded-[4px]"
                  label={
                    <div className="flex items-center justify-start gap-4 text-Accents-Green">
                      <span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="6"
                          height="6"
                          viewBox="0 0 6 6"
                          fill="currentColor"
                        >
                          <rect width="6" height="6" rx="3" />
                        </svg>
                      </span>
                      <span className="text-label-medium-emphasized">
                        {t('Show')}
                      </span>
                    </div>
                  }
                />
              ) : (
                <Tag
                  className="w-fit bg-fills-white border-[1px] border-solid border-outline-default-primary rounded-[4px]"
                  label={
                    <div className="flex items-center justify-start gap-4 text-Accents-yellow">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="6"
                        height="6"
                        viewBox="0 0 6 6"
                        fill="currentColor"
                      >
                        <rect width="6" height="6" rx="3" />
                      </svg>
                      <span className="text-label-medium-emphasized">
                        {t('No show')}
                      </span>
                    </div>
                  }
                />
              )}
            </div>
          );
        },
      },
      {
        title: t('Campaign'),
        dataIndex: 'campaign',
        key: 'campaign',
        width: 240,
        render: (_, record) => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <Tag
                className="w-fit bg-fills-primary rounded-[4px]"
                label={
                  <div className="flex items-center justify-start gap-4">
                    <span className="text-label-medium-emphasized">
                      {record.discount_value
                        ? (() => {
                            if (record.discount_type === 'percent')
                              return `${record.discount_value} %`;
                            else return record.discount_value;
                          })()
                        : 'N/A'}
                    </span>
                  </div>
                }
              />
            </div>
          );
        },
      },
      {
        title: t('Stock'),
        dataIndex: 'total_stock',
        key: 'total_stock',
        width: 120,
        render: value => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <span className="text-body-medium">{value ?? 0}</span>
            </div>
          );
        },
      },
      {
        title: t('Product type'),
        dataIndex: 'product_type',
        key: 'product_type',
        render: value => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <span className="text-body-medium">
                {getValueFromString(value)}
              </span>
            </div>
          );
        },
      },
    ];
  }, [offsetParam]);

  const loadData = useCallback(() => {
    dispatch(
      fetchSaleProducts({
        productId,
        saleType: 'normal',
        offset: offsetParam,
        limit: limitParam,
        kw: kwParam,
        status: 'all',
        country_code,
      })
    );
  }, [offsetParam, limitParam, kwParam, productId, country_code]);

  const handleDelete = useCallback(async () => {
    setIsLoading(true);
    const errorId = [];
    await Promise.allSettled(
      selectedIds?.forEach(id => {
        dispatch(deleteSalteProduct(id)).catch(e => errorId.push(id));
      }) ?? []
    );
    openMessageSuccess({ content: t('Product deleted successfully') });
    if (errorId.length) {
      openMessageError({
        content: `${t('Unable to delete the following codes:')} ${errorId.join(', ')}`,
      });
    }
    setIsLoading(false);
    setSelectedIds([]);
    setTimeout(() => {
      loadData();
    }, 500);
  }, [selectedIds, loadData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadData();
    }, 300);
    return () => clearTimeout(timer);
  }, [offsetParam, limitParam, kwParam, productId]);

  useEffect(() => {
    setSelectedIds([]);
  }, [productId]);

  return (
    <section className="flex p-16 flex-col items-start gap-16 self-stretch rounded-[8px] bg-white relative h-full">
      <div className="flex items-center gap-16 self-stretch">
        <SearchComponent
          placeholder={t('Search product')}
          className="w-[400px]"
          setValue={e => {
            updateSearchParams('kw', e);
          }}
        />
        {!!selectedIds.length && (
          <ButtonPrimaryMedium
            title={t('Delete product')}
            className="bg-Accents-Red disabled:bg-Accents-Red-Fill-Varient"
            onClick={handleDelete}
          />
        )}

        {/* <DropdownComponent
          trigger={['click']}
          menu={{
            items: [],
          }}
          placement="bottom"
        >
          <span className="text-body-medium-emphasized">Tất cả</span>
        </DropdownComponent> */}
      </div>
      <Table
        pagination={false}
        columns={columns.map(item => ({
          ...item,
          render: (value, record, index) => {
            return (
              <Tooltip destroyOnHidden={true} title="Double click to edit">
                {item.render(value, record, index)}
              </Tooltip>
            );
          },
        }))}
        rowKey="id"
        rowSelection={{
          onChange: keys => {
            setSelectedIds([...keys]);
          },
          selectedRowKeys: selectedIds,
        }}
        onRow={(record, rowIndex) => {
          return {
            onClick: event => {}, // click row
            onDoubleClick: event => {
              navigate(
                ROUTER.CMS + ROUTER.UPDATE_PRODUCT_INVENTORY + '/' + record?.id
              );
            }, // double click row
            onContextMenu: event => {}, // right button click row
            onMouseEnter: event => {}, // mouse enter row
            onMouseLeave: event => {}, // mouse leave row
          };
        }}
        dataSource={[...(saleProducts?.data?.[productId]?.products ?? [])]}
        loading={saleProducts?.data?.[productId]?.isLoading || isLoading}
        scroll={{ y: 600 }}
      />
      <div className="flex justify-between items-center self-stretch rounded-[8px] left-0 right-0 bottom-0 mx-[16px] bg-white">
        <Pagination
          // onShowSizeChange={onShowSizeChange}
          className="custom-pagination flex grow [&_.ant-pagination-item-active_a]:!text-white-high"
          current={offsetParam / (limitParam ?? 10) + 1}
          pageSize={limitParam ?? 10} // items per page
          total={total} // total items
          showSizeChanger={{
            placement: 'bottomRight',
            suffixIcon: <Icon.ICExpandMore />,
            options: [
              ...[5, 10, 20, 30].map(item => ({
                label: (
                  <span className="text-body-medium font-semibold !text-black/80">
                    {t('Show') + ' ' + item}
                  </span>
                ),
                value: item,
              })),
            ],
          }} // allow changing page size
          showTotal={(total, range) => (
            <span className="text-body-medium text-black/60">
              {t('Total') + ' item: ' + total}
            </span>
          )}
          pageSizeOptions={[5, 10, 15, 20]}
          position={['bottomCenter']}
          onChange={(page, pageSize) => {
            // handle page change
            updateSearchParams('offset', (page - 1) * pageSize);
            if (limitParam !== pageSize) updateSearchParams('limit', pageSize);
          }}
          prevIcon={
            <ButtonIconOutlineMedium
              className="rounded-[4px] ms-auto"
              icon={<Icon.ICPrev />}
            />
          }
          nextIcon={
            <ButtonIconOutlineMedium
              className="rounded-[4px]"
              icon={<Icon.ICNext />}
            />
          }
        />
      </div>
    </section>
  );
};
